import {
  type DefaultError,
  type QueryKey,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { milliseconds } from 'date-fns';

import { type SchedulingV4GetSlotsResponse, schedulingV4GetSlots } from '@/api/generated';
import { schedulingV4GetSlotsQueryKey } from '@/api/generated/@tanstack/react-query.gen';
import { type TelehealthContactType } from '@/lib/constants';
import type { SchedulingSlot } from '@/types/scheduling.type';

type SchedulingSlotsParams = {
  start: Date;
  end: Date;
  licensedInState: string | null;
  contactType: TelehealthContactType;
  appointmentTypeId: string;
};

function getSchedulingSlotsQueryParts({
  start,
  end,
  licensedInState,
  contactType,
  appointmentTypeId,
}: SchedulingSlotsParams) {
  const startISO = start.toISOString();
  const endISO = end.toISOString();
  const v4Query = {
    start: startISO,
    end: endISO,
    licensed_in_state: licensedInState,
    contact_type: contactType,
    appointment_type_id: appointmentTypeId,
  };
  const queryKey: QueryKey = schedulingV4GetSlotsQueryKey({ query: v4Query });
  return { queryKey, v4Query };
}

/** TanStack query key for GET /api/v4/scheduling/slots (Healthie). */
export function getSchedulingSlotsQueryKey(params: SchedulingSlotsParams): QueryKey {
  return getSchedulingSlotsQueryParts(params).queryKey;
}

type SchedulingSlotsQueryCallbacks = Omit<
  UseQueryOptions<SchedulingV4GetSlotsResponse, DefaultError, SchedulingSlot[]>,
  'queryKey' | 'queryFn' | 'select' | 'staleTime' | 'refetchInterval'
>;

function useGetSchedulingSlotsQuery(
  params: SchedulingSlotsParams,
  options?: SchedulingSlotsQueryCallbacks
) {
  const { queryKey, v4Query } = getSchedulingSlotsQueryParts(params);
  const { contactType, appointmentTypeId } = params;

  const query = useQuery<SchedulingV4GetSlotsResponse, DefaultError, SchedulingSlot[]>({
    queryKey,
    queryFn: async ({ signal }) => {
      const { data } = await schedulingV4GetSlots({
        query: v4Query,
        signal,
        throwOnError: true,
      });
      return data;
    },
    staleTime: 0,
    refetchInterval: milliseconds({ minutes: 5 }),
    select: (data) => transformToSchedulingSlots(data.payload, contactType, appointmentTypeId),
    ...options,
  });

  return { ...query, queryKey };
}

const transformToSchedulingSlots = (
  payload: SchedulingV4GetSlotsResponse['payload'],
  contactType: SchedulingSlotsParams['contactType'],
  appointmentTypeId: SchedulingSlotsParams['appointmentTypeId']
): SchedulingSlot[] => {
  return Object.entries(payload)
    .map(([date, slots]) => ({
      date,
      slots: slots.map((slot) => {
        const providerId =
          'provider_id' in slot && slot.provider_id != null ? String(slot.provider_id) : undefined;
        return {
          start: slot.start,
          ...(providerId && { providerId }),
          contactType,
          appointmentTypeId,
        };
      }),
    }))
    .slice(0, 7) // limit to first 7 days
    .sort((a, b) => a.date.localeCompare(b.date));
};

export { useGetSchedulingSlotsQuery };
